import * as React from "react"
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"
import { CircleIcon } from "lucide-react"

import { cn } from "../../lib/utils"

export type RadioGroupProps = React.ComponentProps<typeof RadioGroupPrimitive.Root> & {error?: string, label?: string};

function RadioGroup({
  error,
  className,
  label,
  ...props
}: RadioGroupProps) {
  return (
    <div className="flex flex-col w-full gap-4">
      {label && (
        <p>
          {label}
        </p>
      )}
    <RadioGroupPrimitive.Root
      data-slot="radio-group"
      className={cn("grid gap-3", className)}
      {...props}
      />
      {error && (
        <p className="text-destructive mt-2 text-xs" role="alert" aria-live="polite">
          {error}
        </p>
      )}
      </div>
  )
}

interface CustomRadioGroupItemProps extends React.ComponentProps<typeof RadioGroupPrimitive.Item> {
  label?: string;
  fillInside?: boolean;
}

function RadioGroupItem({
  className,
  id,
  label,
  fillInside,
  ...props
}: CustomRadioGroupItemProps) {
  const generatedId = React.useId();
  const elementId = id || generatedId;
  
  return (
    <div className="flex items-center space-x-2 w-full">
      <RadioGroupPrimitive.Item
        data-slot="radio-group-item"
        className={cn(
          "border-input text-primary focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 aspect-square size-4 shrink-0 rounded-full border shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
          fillInside && "data-[state='checked']:bg-primary",
          className
        )}
        id={elementId}
        {...props}
        >
        <RadioGroupPrimitive.Indicator
          data-slot="radio-group-indicator"
          className="relative flex items-center justify-center"
          >
          <CircleIcon
            className={cn(
              "absolute top-1/2 left-1/2 size-2 -translate-x-1/2 -translate-y-1/2",
              fillInside ? "fill-white" : "fill-primary"
            )}
            />
        </RadioGroupPrimitive.Indicator>
      </RadioGroupPrimitive.Item>
      {label && (
        <label 
          htmlFor={elementId} 
          className="cursor-pointer select-none"
        >
          {label}
        </label>
      )}
    </div>
  )
}

export { RadioGroup, RadioGroupItem }
